import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Fingerprint,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Plug,
  Link2,
  ScrollText,
  Settings2,
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import SettingsSectionHeader from '../SettingsSectionHeader';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';
const EMPTY_DEVICE = {
  name: '',
  brand: 'zkteco',
  model: '',
  serial_number: '',
  device_uid: '',
  connection_type: 'push',
  protocol: 'https',
  ip_address: '',
  port: '',
  server_url: '',
  api_username: '',
  api_password: '',
  api_token: '',
  device_secret: '',
  enabled: true,
};

function formatDt(v) {
  if (!v) return '—';
  try {
    return new Date(v.replace(' ', 'T')).toLocaleString();
  } catch {
    return v;
  }
}

function statusBadgeClass(status) {
  switch (String(status || '').toLowerCase()) {
    case 'online':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'offline':
      return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'error':
      return 'text-red-700 bg-red-50 border-red-200';
    case 'disabled':
      return 'text-muted-foreground bg-muted border-border';
    default:
      return 'text-foreground bg-background border-border';
  }
}

export default function BiometricAttendanceSection() {
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [devices, setDevices] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [busy, setBusy] = useState(false);

  // Device form
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_DEVICE);
  const [onceSecret, setOnceSecret] = useState('');

  // Mappings
  const [mapDeviceId, setMapDeviceId] = useState('');
  const [mappings, setMappings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [mapClassId, setMapClassId] = useState('');
  const [mapSectionId, setMapSectionId] = useState('');
  const [mapSearch, setMapSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [mapFilter, setMapFilter] = useState('all'); // all | mapped | unmapped

  // Logs
  const [logDeviceId, setLogDeviceId] = useState('');
  const [logs, setLogs] = useState([]);

  const selectedClass = useMemo(
    () => classes.find((c) => String(c.id) === String(mapClassId)),
    [classes, mapClassId],
  );
  const sections = selectedClass?.sections || [];

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, setRes, devRes, syncRes, unRes] = await Promise.all([
        api.get('/biometric/summary'),
        api.get('/biometric/settings'),
        api.get('/biometric/devices'),
        api.get('/biometric/sync-logs', { params: { limit: 20 } }),
        api.get('/biometric/unmapped', { params: { limit: 50 } }),
      ]);
      setSummary(sumRes.data?.data || null);
      setSettings(setRes.data?.data?.settings || null);
      setDevices(devRes.data?.data?.devices || []);
      setSyncLogs(syncRes.data?.data?.sync_logs || []);
      setUnmapped(unRes.data?.data?.unmapped || []);
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to load biometric data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (tab !== 'mappings') return;
    (async () => {
      try {
        const res = await api.get('/classes');
        setClasses(res.data?.data || []);
      } catch {
        /* ignore */
      }
    })();
  }, [tab]);

  useEffect(() => {
    if (!mapDeviceId) {
      setMappings([]);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/biometric/devices/${mapDeviceId}/mappings`);
        setMappings(res.data?.data?.mappings || []);
      } catch {
        setMappings([]);
      }
    })();
  }, [mapDeviceId]);

  useEffect(() => {
    if (!mapClassId) {
      setStudents([]);
      return;
    }
    (async () => {
      try {
        const params = { class_id: mapClassId };
        if (mapSectionId) params.section_id = mapSectionId;
        const res = await api.get('/students', { params });
        setStudents(res.data?.data || []);
      } catch {
        setStudents([]);
      }
    })();
  }, [mapClassId, mapSectionId]);

  useEffect(() => {
    if (!logDeviceId) {
      setLogs([]);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/biometric/devices/${logDeviceId}/logs`, { params: { limit: 100 } });
        setLogs(res.data?.data?.logs || []);
      } catch {
        setLogs([]);
      }
    })();
  }, [logDeviceId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_DEVICE, device_uid: `DEV-${Date.now()}` });
    setOnceSecret('');
    setFormOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '',
      brand: d.brand || 'zkteco',
      model: d.model || '',
      serial_number: d.serial_number || '',
      device_uid: d.device_uid || '',
      connection_type: d.connection_type || 'push',
      protocol: d.protocol || 'https',
      ip_address: d.ip_address || '',
      port: d.port ?? '',
      server_url: d.server_url || '',
      api_username: d.api_username || '',
      api_password: '',
      api_token: '',
      device_secret: '',
      enabled: !!Number(d.enabled),
    });
    setOnceSecret('');
    setFormOpen(true);
  };

  const saveDevice = async () => {
    if (!form.name?.trim() || !form.device_uid?.trim()) {
      toast({ title: 'Validation', description: 'Name and device identifier are required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        port: form.port === '' || form.port == null ? null : Number(form.port),
      };
      if (!payload.api_password) delete payload.api_password;
      if (!payload.api_token) delete payload.api_token;
      if (!payload.device_secret) delete payload.device_secret;

      let res;
      if (editing) {
        res = await api.put(`/biometric/devices/${editing.id}`, payload);
      } else {
        res = await api.post('/biometric/devices', payload);
      }
      const device = res.data?.data?.device;
      if (device?.device_secret_once) {
        setOnceSecret(device.device_secret_once);
      }
      toast({ title: 'Saved', description: editing ? 'Device updated' : 'Device created' });
      if (!device?.device_secret_once) setFormOpen(false);
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to save device',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const deleteDevice = async (d) => {
    if (!window.confirm(`Delete device "${d.name}"? Mappings will be removed. Logs are kept.`)) return;
    setBusy(true);
    try {
      await api.delete(`/biometric/devices/${d.id}`);
      toast({ title: 'Deleted', description: 'Device removed' });
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const testDevice = async (d) => {
    setBusy(true);
    try {
      const res = await api.post(`/biometric/devices/${d.id}/test`);
      const ok = res.data?.data?.success;
      toast({
        title: ok ? 'Connection OK' : 'Connection issue',
        description: res.data?.data?.message || res.data?.message || '',
        variant: ok ? 'default' : 'destructive',
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Unable to connect to biometric device.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const syncDevice = async (d) => {
    setBusy(true);
    try {
      const res = await api.post(`/biometric/devices/${d.id}/sync`);
      toast({ title: 'Sync', description: res.data?.message || 'Sync completed' });
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Synchronization failed.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const syncAllNow = async () => {
    setBusy(true);
    try {
      const res = await api.post('/biometric/sync-now');
      toast({ title: 'Sync', description: res.data?.message || 'Synchronization finished' });
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Synchronization failed.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.post('/biometric/settings', { settings });
      toast({ title: 'Saved', description: 'Biometric settings saved' });
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const mappedStudentIds = useMemo(
    () => new Set(mappings.map((m) => String(m.student_id))),
    [mappings],
  );

  const filteredStudents = useMemo(() => {
    let list = students;
    if (mapSearch.trim()) {
      const q = mapSearch.trim().toLowerCase();
      list = list.filter((s) => {
        const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
        return (
          name.includes(q)
          || String(s.admission_no || '').toLowerCase().includes(q)
          || String(s.roll_no || '').toLowerCase().includes(q)
        );
      });
    }
    if (mapFilter === 'mapped') list = list.filter((s) => mappedStudentIds.has(String(s.id)));
    if (mapFilter === 'unmapped') list = list.filter((s) => !mappedStudentIds.has(String(s.id)));
    return list;
  }, [students, mapSearch, mapFilter, mappedStudentIds]);

  const saveMapping = async (student, biometricUserId) => {
    if (!mapDeviceId) {
      toast({ title: 'Select a device first', variant: 'destructive' });
      return;
    }
    const uid = (biometricUserId || student.admission_no || '').trim();
    if (!uid) {
      toast({ title: 'Biometric User ID required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await api.post('/biometric/mappings', {
        device_id: Number(mapDeviceId),
        student_id: Number(student.id),
        biometric_user_id: uid,
        biometric_name: `${student.first_name || ''} ${student.last_name || ''}`.trim(),
      });
      toast({ title: 'Mapped', description: `${student.first_name} → ${uid}` });
      const res = await api.get(`/biometric/devices/${mapDeviceId}/mappings`);
      setMappings(res.data?.data?.mappings || []);
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to map student',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const removeMapping = async (m) => {
    if (!window.confirm('Remove this mapping?')) return;
    setBusy(true);
    try {
      await api.delete(`/biometric/mappings/${m.id}`);
      toast({ title: 'Removed' });
      if (mapDeviceId) {
        const res = await api.get(`/biometric/devices/${mapDeviceId}/mappings`);
        setMappings(res.data?.data?.mappings || []);
      }
      await loadCore();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to remove mapping',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const bulkMapAdmission = async () => {
    if (!mapDeviceId) return;
    const candidates = filteredStudents.filter((s) => !mappedStudentIds.has(String(s.id)) && s.admission_no);
    if (!candidates.length) {
      toast({ title: 'Nothing to map', description: 'No unmapped students with admission numbers in the current filter.' });
      return;
    }
    if (!window.confirm(`Map ${candidates.length} student(s) using admission number as biometric user ID?`)) return;
    setBusy(true);
    let ok = 0;
    let fail = 0;
    for (const s of candidates) {
      try {
        await api.post('/biometric/mappings', {
          device_id: Number(mapDeviceId),
          student_id: Number(s.id),
          biometric_user_id: String(s.admission_no),
          biometric_name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
        });
        ok++;
      } catch {
        fail++;
      }
    }
    toast({ title: 'Bulk mapping', description: `Mapped ${ok}, failed ${fail}` });
    const res = await api.get(`/biometric/devices/${mapDeviceId}/mappings`);
    setMappings(res.data?.data?.mappings || []);
    await loadCore();
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <SettingsSectionHeader
        sectionId="academic.biometric"
        description="Configure school biometric devices, map students, and sync attendance punches."
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="mappings">Mappings</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="unmapped">Unmapped</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Devices', value: summary?.devices ?? 0 },
              { label: 'Online', value: summary?.online ?? 0 },
              { label: 'Offline', value: summary?.offline ?? 0 },
              { label: "Today's punches", value: summary?.todays_punches ?? 0 },
              { label: 'Mapped students', value: summary?.mapped_students ?? 0 },
              { label: 'Unmapped punches', value: summary?.unmapped_punches ?? 0 },
              { label: 'Sync errors', value: summary?.sync_errors ?? 0 },
              { label: 'Last sync', value: formatDt(summary?.last_sync) },
            ].map((item) => (
              <Card key={item.label} className="border-border">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-lg font-semibold text-foreground mt-1">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={syncAllNow} disabled={busy} className={PRIMARY_BTN}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync Now
            </Button>
            <Button variant="outline" onClick={loadCore}>
              Refresh
            </Button>
          </div>
          <Card className="border-border">
            <CardContent className="p-4 space-y-2">
              <p className="font-medium text-foreground flex items-center gap-2">
                <ScrollText className="w-4 h-4" /> Recent synchronization
              </p>
              {syncLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sync history yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Started</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Found</TableHead>
                        <TableHead>Inserted</TableHead>
                        <TableHead>Duplicate</TableHead>
                        <TableHead>Failed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncLogs.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{formatDt(r.sync_started_at)}</TableCell>
                          <TableCell>#{r.device_id}</TableCell>
                          <TableCell className="capitalize">{r.status}</TableCell>
                          <TableCell>{r.records_found}</TableCell>
                          <TableCell>{r.records_inserted}</TableCell>
                          <TableCell>{r.records_duplicate}</TableCell>
                          <TableCell>{r.records_failed}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card className="border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="font-medium">Enable Biometric Attendance</p>
                  <p className="text-xs text-muted-foreground">When off, push/sync is rejected for this school.</p>
                </div>
                <Switch
                  checked={['1', 'true', 'yes', 'on'].includes(String(settings?.biometric_enabled || '').toLowerCase())}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, biometric_enabled: v ? '1' : '0' }))}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Sync mode</Label>
                  <Select
                    value={settings?.biometric_sync_mode || 'automatic'}
                    onValueChange={(v) => setSettings((s) => ({ ...s, biometric_sync_mode: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">Automatic</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sync interval (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={settings?.biometric_sync_interval_minutes ?? '5'}
                    onChange={(e) => setSettings((s) => ({ ...s, biometric_sync_interval_minutes: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Timezone</Label>
                  <Input
                    value={settings?.biometric_timezone || 'Asia/Kolkata'}
                    onChange={(e) => setSettings((s) => ({ ...s, biometric_timezone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Clock skew warning (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={settings?.biometric_clock_skew_warning_minutes ?? '10'}
                    onChange={(e) => setSettings((s) => ({ ...s, biometric_clock_skew_warning_minutes: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Default device ID</Label>
                  <Select
                    value={settings?.biometric_default_device_id || 'none'}
                    onValueChange={(v) => setSettings((s) => ({ ...s, biometric_default_device_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {devices.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={saveSettings} disabled={busy} className={PRIMARY_BTN}>
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">School-scoped devices. Credentials are never shown after save.</p>
            <Button onClick={openCreate} className={PRIMARY_BTN}>
              <Plus className="w-4 h-4 mr-2" /> Add Device
            </Button>
          </div>
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Brand / Model</TableHead>
                    <TableHead>Device ID</TableHead>
                    <TableHead>Connection</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last sync</TableHead>
                    <TableHead>Mapped</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No devices configured.
                      </TableCell>
                    </TableRow>
                  ) : devices.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="capitalize">{d.brand}{d.model ? ` / ${d.model}` : ''}</TableCell>
                      <TableCell className="font-mono text-xs">{d.device_uid}</TableCell>
                      <TableCell className="capitalize">{d.connection_type}</TableCell>
                      <TableCell>
                        <span className={`inline-flex text-xs px-2 py-0.5 rounded border ${statusBadgeClass(d.display_status || d.status)}`}>
                          {(d.display_status || d.status || '—').replace(/_/g, ' ')}
                        </span>
                        {d.last_error ? (
                          <p className="text-xs text-red-600 mt-1 max-w-[180px] truncate" title={d.last_error}>{d.last_error}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs">{formatDt(d.last_successful_sync_at)}</TableCell>
                      <TableCell>{d.mapped_students ?? 0}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Button size="sm" variant="outline" onClick={() => openEdit(d)}>Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => testDevice(d)} disabled={busy}>
                            <Plug className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => syncDevice(d)} disabled={busy}>
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setMapDeviceId(String(d.id)); setTab('mappings'); }}>
                            <Link2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setLogDeviceId(String(d.id)); setTab('logs'); }}>
                            <ScrollText className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteDevice(d)} disabled={busy}>
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mappings" className="space-y-4">
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label>Device</Label>
              <Select value={mapDeviceId || 'none'} onValueChange={(v) => setMapDeviceId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select device" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select device</SelectItem>
                  {devices.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={mapClassId || 'none'} onValueChange={(v) => { setMapClassId(v === 'none' ? '' : v); setMapSectionId(''); }}>
                <SelectTrigger><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All / pick class</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name || c.class_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Section</Label>
              <Select value={mapSectionId || 'none'} onValueChange={(v) => setMapSectionId(v === 'none' ? '' : v)} disabled={!sections.length}>
                <SelectTrigger><SelectValue placeholder="Section" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name || s.section_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filter</Label>
              <Select value={mapFilter} onValueChange={setMapFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All students</SelectItem>
                  <SelectItem value="mapped">Mapped</SelectItem>
                  <SelectItem value="unmapped">Unmapped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[180px]">
              <Label>Search</Label>
              <Input value={mapSearch} onChange={(e) => setMapSearch(e.target.value)} placeholder="Name, admission no, roll" />
            </div>
            <Button variant="outline" onClick={bulkMapAdmission} disabled={!mapDeviceId || busy}>
              Bulk map by admission no
            </Button>
          </div>

          {mapDeviceId ? (
            <Card className="border-border">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Admission</TableHead>
                      <TableHead>Roll</TableHead>
                      <TableHead>Biometric User ID</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((s) => {
                      const existing = mappings.find((m) => String(m.student_id) === String(s.id));
                      return (
                        <TableRow key={s.id}>
                          <TableCell>{s.first_name} {s.last_name}</TableCell>
                          <TableCell>{s.admission_no || '—'}</TableCell>
                          <TableCell>{s.roll_no || '—'}</TableCell>
                          <TableCell>
                            {existing ? (
                              <span className="font-mono text-sm">{existing.biometric_user_id}</span>
                            ) : (
                              <Input
                                className="max-w-[160px]"
                                defaultValue={s.admission_no || ''}
                                id={`bio-uid-${s.id}`}
                                placeholder="STU1025"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {existing ? (
                              <Button size="sm" variant="outline" onClick={() => removeMapping(existing)} disabled={busy}>
                                Remove
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className={PRIMARY_BTN}
                                disabled={busy}
                                onClick={() => {
                                  const el = document.getElementById(`bio-uid-${s.id}`);
                                  saveMapping(s, el?.value);
                                }}
                              >
                                Save mapping
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {mapClassId && filteredStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-6">No students match filters.</TableCell>
                      </TableRow>
                    ) : null}
                    {!mapClassId ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-6">Select a class to list students.</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">Select a device to manage mappings.</p>
          )}

          {mappings.length > 0 && (
            <Card className="border-border">
              <CardContent className="p-4">
                <p className="font-medium mb-2">Existing mappings for device</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Admission</TableHead>
                      <TableHead>Biometric ID</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappings.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.first_name} {m.last_name}</TableCell>
                        <TableCell>{m.admission_no}</TableCell>
                        <TableCell className="font-mono">{m.biometric_user_id}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => removeMapping(m)}>Remove</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <div className="max-w-sm">
            <Label>Device</Label>
            <Select value={logDeviceId || 'none'} onValueChange={(v) => setLogDeviceId(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select device" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select device</SelectItem>
                {devices.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device time</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Punch</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{formatDt(l.device_event_time)}</TableCell>
                      <TableCell>{formatDt(l.server_received_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{l.biometric_user_id}</TableCell>
                      <TableCell>{l.student_id || '—'}</TableCell>
                      <TableCell className="capitalize">{l.sync_status}</TableCell>
                      <TableCell>{l.punch_type || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!logDeviceId ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Select a device.</TableCell></TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No logs.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unmapped" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Punches from biometric user IDs that are not mapped to students. Map them under Mappings.
          </p>
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unmapped.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">No unmapped punches.</TableCell>
                    </TableRow>
                  ) : unmapped.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{formatDt(u.device_event_time)}</TableCell>
                      <TableCell>{u.device_name || `#${u.device_id}`}</TableCell>
                      <TableCell className="font-mono">{u.biometric_user_id}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setMapDeviceId(String(u.device_id));
                            setTab('mappings');
                          }}
                        >
                          Map student
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5" />
              {editing ? 'Edit device' : 'Add biometric device'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Device name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Brand</Label>
                <Select value={form.brand} onValueChange={(v) => setForm((f) => ({ ...f, brand: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zkteco">ZKTeco</SelectItem>
                    <SelectItem value="essl">eSSL</SelectItem>
                    <SelectItem value="matrix">Matrix</SelectItem>
                    <SelectItem value="hikvision">Hikvision</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Model</Label>
                <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Serial number</Label>
                <Input value={form.serial_number} onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))} />
              </div>
              <div>
                <Label>Device identifier</Label>
                <Input value={form.device_uid} onChange={(e) => setForm((f) => ({ ...f, device_uid: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Connection type</Label>
                <Select value={form.connection_type} onValueChange={(v) => setForm((f) => ({ ...f, connection_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="push">Push</SelectItem>
                    <SelectItem value="pull">Pull / API</SelectItem>
                    <SelectItem value="sdk_agent">SDK / Local agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Protocol</Label>
                <Select value={form.protocol || 'https'} onValueChange={(v) => setForm((f) => ({ ...f, protocol: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="https">HTTPS</SelectItem>
                    <SelectItem value="http">HTTP</SelectItem>
                    <SelectItem value="tcp">TCP</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(form.connection_type === 'pull') && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Device IP</Label>
                    <Input value={form.ip_address} onChange={(e) => setForm((f) => ({ ...f, ip_address: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>Server / API URL</Label>
                  <Input value={form.server_url} onChange={(e) => setForm((f) => ({ ...f, server_url: e.target.value }))} />
                </div>
                <div>
                  <Label>API username</Label>
                  <Input value={form.api_username} onChange={(e) => setForm((f) => ({ ...f, api_username: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>API password {editing?.api_password_set ? '(set)' : ''}</Label>
                    <Input type="password" placeholder={editing?.api_password_set ? '••••••' : ''} value={form.api_password} onChange={(e) => setForm((f) => ({ ...f, api_password: e.target.value }))} />
                  </div>
                  <div>
                    <Label>API token {editing?.api_token_set ? '(set)' : ''}</Label>
                    <Input type="password" placeholder={editing?.api_token_set ? '••••••' : ''} value={form.api_token} onChange={(e) => setForm((f) => ({ ...f, api_token: e.target.value }))} />
                  </div>
                </div>
              </>
            )}

            {(form.connection_type === 'push' || form.connection_type === 'sdk_agent') && (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground flex items-center gap-1"><Settings2 className="w-3 h-3" /> Push / agent endpoint</p>
                  <p className="font-mono break-all">POST /api/biometric/push/{'{device_uid}'}/events</p>
                  <p>Header: <span className="font-mono">X-Device-Secret: &lt;secret&gt;</span></p>
                  <p>Optional Server URL field for your local agent config:</p>
                </div>
                <div>
                  <Label>Server / API URL (agent config)</Label>
                  <Input value={form.server_url} onChange={(e) => setForm((f) => ({ ...f, server_url: e.target.value }))} placeholder="https://your-domain/api" />
                </div>
                <div>
                  <Label>Device secret {editing?.device_secret_set ? '(leave blank to keep)' : '(auto-generated if empty)'}</Label>
                  <Input type="password" value={form.device_secret} onChange={(e) => setForm((f) => ({ ...f, device_secret: e.target.value }))} placeholder={editing?.device_secret_set ? '••••••' : 'Optional — auto if blank'} />
                </div>
              </>
            )}

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Enabled</Label>
              <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            </div>

            {onceSecret ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                <p className="font-medium text-amber-900">Copy device secret now — it will not be shown again.</p>
                <p className="font-mono text-xs break-all mt-1">{onceSecret}</p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Close</Button>
            <Button onClick={saveDevice} disabled={busy} className={PRIMARY_BTN}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
